import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { useSellerInfo } from '../../../../app/hooks/useSellerInfo'
import { formatAccountAndAgency } from '../../../../app/utils/formatAccountAndAgency'
import { formatPhoneNumber } from '../../../../app/utils/formatPhoneNumber'
import { formatZipCode } from '../../../../app/utils/formatZipCode'
import { useTransactions } from '@/app/hooks/useTransactions'

const schema = z.object({
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  birthDate: z.string(),
  address: z.object({
    zipCode: z.string().optional(),
    state: z.string().optional(),
    city: z.string().optional(),
    neighborhood: z.string().optional(),
    street: z.string().optional(),
    number: z.string().optional(),
    complement: z.string().optional(),
  }),
  bankAccount: z.object({
    payday: z.coerce.number().optional(),
    bank: z.string().optional(),
    agency: z.string().optional(),
    account: z.string().optional(),
    accountType: z.string().optional(),
    pixKey: z.string().optional(),
    pixType: z.string().optional(),
  }),
  commissionRate: z.coerce.number().optional(),
})

export type FormSellerData = z.infer<typeof schema>

export const useSellerDetailsPageController = () => {
  const { id } = useParams()
  const methods = useForm<FormSellerData>({
    resolver: zodResolver(schema),
  })

  const { data, isFetching, isLoading } = useSellerInfo(id!)

  const { data: transaction, setParams } = useTransactions({})

  const { reset } = methods

  useEffect(() => {
    setParams({
      sellerIds: id,
    })
  }, [id])

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        birthDate: data.birthDate ? format(data.birthDate, 'dd/MM/yyyy') : '',
        phone: formatPhoneNumber(data.phone),
        address: {
          ...data.address,
          zipCode: formatZipCode(data.address.zipCode),
        },
        bankAccount: {
          ...data.bankAccount,
          account: formatAccountAndAgency(data.bankAccount.account),
        },
      })
    }
  }, [reset, data])

  return {
    data,
    transaction,
    isFetching,
    isLoading,
    methods,
  }
}
